<template>
  <footer ref="footerRef" class="hidden h-footer justify-between bg-emo-black px-6 text-white md:flex">
    <div class="flex min-w-0 items-center">
      <div ref="leftContentRef" class="flex shrink-0 items-center gap-4 whitespace-nowrap">
        <router-link :to="textVersionButtonContent.routePath">
          <button class="flex h-8 items-center gap-1 rounded-[4px] border border-white px-2">
            <gv-icon :name="textVersionButtonContent.icon" class="fill-white" />
            <div>{{ textVersionButtonContent.content }}</div>
          </button>
        </router-link>
        <nav class="flex">
          <div v-for="(appRoute, index) in footerRoutes" :key="appRoute.title" class="flex">
            <ui-footer-nav-item v-if="appRoute.routeType == 'internal'" :link="appRoute.link" :title="appRoute.title" />
            <a v-else-if="appRoute.routeType == 'external'" class="underline" :href="appRoute.link" target="_blank" external>{{
              appRoute.title
            }}</a>
            <div v-if="index !== footerRoutes.length - 1" class="mx-4 h-6 w-px bg-emo-mid-grey" />
          </div>
          <div class="mx-4 h-6 w-px bg-emo-mid-grey" />
          <ui-language-dropdown />
        </nav>
      </div>
    </div>

    <div ref="rightContentRef" class="flex shrink-0 items-center gap-4">
      <template v-if="showStoreBadges">
        <ui-store-badges />

        <div class="h-6 w-px bg-emo-mid-grey" />
      </template>

      <a href="https://www.gov.mb.ca/index.html" target="_blank">
        <img src="@/common/assets/logos/manitoba-government-logo.png" :alt="t('C8vUemFpTH3cavAdPjofJ')" class="h-4" />
      </a>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { useResizeObserver } from '@vueuse/core';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const { t } = useI18n();
const route = useRoute();

/** Smallest acceptable gap between the left-hand footer content and the store badges. */
const MIN_CONTENT_GAP = 8;

const footerRef = ref<HTMLElement>();
const leftContentRef = ref<HTMLElement>();
const rightContentRef = ref<HTMLElement>();

const showStoreBadges = ref(true);

// The width the right-hand content needs *with* the badges present, cached while they are rendered
// — once hidden the group measures narrower, so its live width can no longer answer the question.
let rightContentWidth = 0;

const updateStoreBadgeVisibility = () => {
  const footer = footerRef.value;
  const leftContent = leftContentRef.value;
  const rightContent = rightContentRef.value;

  if (!footer || !leftContent || !rightContent) return;

  if (showStoreBadges.value && rightContent.offsetWidth) rightContentWidth = rightContent.offsetWidth;
  if (!rightContentWidth) return;

  const { paddingLeft, paddingRight } = getComputedStyle(footer);
  const availableWidth = footer.clientWidth - parseFloat(paddingLeft) - parseFloat(paddingRight);

  // The left content never shrinks and the right-hand width is cached, so neither input depends on
  // whether the badges are currently shown — the result cannot oscillate between the two states.
  const requiredWidth = leftContent.offsetWidth + MIN_CONTENT_GAP + rightContentWidth;

  showStoreBadges.value = availableWidth >= requiredWidth;
};

useResizeObserver([footerRef, leftContentRef, rightContentRef], updateStoreBadgeVisibility);

const textVersionButtonContent = computed(() => {
  if (route.fullPath.startsWith('/text-version')) {
    return {
      content: t('lguL1fqTJzX7folpDEoBI'),
      icon: 'web',
      routePath: '/',
    };
  }
  return {
    content: t('worQ16AQn448N1wCt-tLQ'),
    icon: 'abc',
    routePath: '/text-version',
  };
});

const footerRoutes = computed(() => {
  return [
    {
      title: t('1a7LOuqd0nGD2JxQBAq2-'),
      link: '/about',
      routeType: 'internal',
    },
    {
      title: t('cVpCn6Y4HFbwfn92yjYSb'),
      link: '/about#disclaimer',
      routeType: 'internal',
    },
  ];
});
</script>
